import React from 'react'
import { StyleSheet, View, Text } from 'react-native'
import { IObject } from 'interfaces'
import { BaseObjectProps } from '../BaseObject'
import TableScroll from './TableScroll'
import PaginatedFetchComponent, {
  connectPaginatedFetch,
  PaginatedFetchProps,
} from './PaginatedFetchComponent'
import { getBindingData } from './utils'
import { BindingDataItem, Field } from './TableTypes'
import TableRow, { Row } from './TableRow'
import PaginationControls from './PaginationControls'

type TableAttributes = {
  attributes: {
    fields: Field[]
    pageSize?: number
  }
}

interface TableObjectProps extends BaseObjectProps {
  bindingData: BindingDataItem[]
  object: IObject & TableAttributes
}

type TableState = {
  page: number
}

const DEFAULT_PAGE_LIMIT = 10
const PREFETCH_NEXT_PAGE = true

type Props = TableObjectProps & PaginatedFetchProps
class Table extends PaginatedFetchComponent<Props, TableState> {
  constructor(props: Props) {
    super(props)

    this.state = { page: 0 }
  }

  getPageSize() {
    const { object } = this.props
    const { attributes } = object

    const pageSize = attributes?.pageSize ?? DEFAULT_PAGE_LIMIT

    return pageSize
  }

  getTableColumns() {
    const { bindingData, object } = this.props
    const fields = object?.attributes.fields

    if (bindingData && fields) {
      const { columns } = getBindingData(fields, bindingData)

      return columns
    }

    return []
  }

  getTableDataRowsForPage(page: number, pageSize: number) {
    const { bindingData, object } = this.props
    const fields = object?.attributes.fields

    if (bindingData && fields) {
      const { data } = getBindingData(fields, bindingData)

      // Hacky way to use pagination on local state
      const pageData = data.slice(page * pageSize, (page + 1) * pageSize)

      return pageData
    }

    return []
  }

  getTableDataRows() {
    const { page } = this.state
    const pageSize = this.getPageSize()
    return this.getTableDataRowsForPage(page, pageSize)
  }

  renderPaginationControls() {
    const { page } = this.state
    const { object } = this.props
    const pageSize = this.getPageSize()

    const goToNextPage = () => {
      const { dataBinding } = object

      if (!dataBinding) {
        throw new Error(
          `Tried to fetch data from Table without dataBinding. Object: ${object.id}`
        )
      }

      const nextPage = page + 1
      const nextPageDataLoaded = this.getTableDataRowsForPage(
        nextPage,
        pageSize
      )

      if (nextPageDataLoaded.length === 0) {
        this.fetchPageData(nextPage, pageSize)
      }

      if (PREFETCH_NEXT_PAGE) {
        this.fetchPageData(nextPage + 1, pageSize)
      }

      this.setState({ page: nextPage })
    }

    const goToPreviousPage = () => {
      if (page !== 0) {
        this.setState({ page: page - 1 })
      }
    }

    const dataItemCount = this.getTableDataRows().length
    const nextEnabled = dataItemCount >= pageSize

    return (
      <PaginationControls
        nextDisabled={!nextEnabled}
        currentPage={page}
        goToNextPage={goToNextPage}
        goToPreviousPage={goToPreviousPage}
      />
    )
  }

  render() {
    const data = this.getTableDataRows()
    const columns = this.getTableColumns()

    if (columns.length === 0 || data.length === 0) {
      return null
    }

    const isLastRow = (index: number) => index === data.length - 1

    const tableMinWidth = columns.reduce((acc, c) => acc + c.style.minWidth, 0)

    return (
      <View style={styles.tableContainer}>
        <TableScroll horizontal>
          <View style={[styles.tableLayout, { minWidth: tableMinWidth }]}>
            <View style={styles.header}>
              {columns.map(({ label, style }) => (
                <View style={[styles.headerCell, style]} key={label}>
                  <Text style={styles.headerText}>{label}</Text>
                </View>
              ))}
            </View>
            {data.map((rowData: Row, rowIndex: number) => (
              <TableRow
                key={rowData.id}
                data={rowData}
                isLastRow={isLastRow(rowIndex)}
              />
            ))}
          </View>
        </TableScroll>
        {this.renderPaginationControls()}
      </View>
    )
  }
}

const styles = StyleSheet.create({
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 5,
    elevation: 5,
    backgroundColor: '#fff',
  },
  tableLayout: {
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerCell: {
    flex: 1,
    height: 50,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F6F6F6',
  },
  headerText: {
    fontWeight: 'bold',
    color: '#333',
  },
  disabledText: {
    color: '#ccc',
  },
})

// @ts-ignore
export default connectPaginatedFetch(Table)
